import React, { useEffect, useState } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { HouseSimple, Hospital, UsersThree, GearSix, ShieldCheck } from '@phosphor-icons/react';
import Sidebar from './Sidebar';
import Avatar from './Avatar';
import { tap } from './ui';
import { useAuth } from '../lib/auth';
import { isNative } from '../lib/platform';

const tabs = [
  { path: '/', label: 'Home', icon: HouseSimple },
  { path: '/clinics', label: 'Clinics', icon: Hospital },
  { path: '/clinicians', label: 'Clinicians', icon: UsersThree },
  { path: '/settings', label: 'Settings', icon: GearSix },
];

export default function Layout() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const showAdminTab = profile?.user_type === 'super_admin' && !isNative;
  const mobileTabs = showAdminTab
    ? [...tabs, { path: '/admin/users', label: 'Admin', icon: ShieldCheck }]
    : tabs;

  // Phone header turns frosted once content scrolls under it
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // New screen starts at the top
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);

  return (
    <div className="flex flex-col md:flex-row min-h-screen bg-[#F6F5FA]">
      {/* Phone header */}
      <header
        className={`md:hidden fixed top-0 left-0 right-0 z-20 transition-[background-color,box-shadow] duration-200 ${
          scrolled ? 'bg-[#F6F5FA]/80 backdrop-blur-xl shadow-[0_1px_0_rgba(17,12,34,0.06)]' : 'bg-[#F6F5FA]'
        }`}
        style={{ paddingTop: 'env(safe-area-inset-top)' }}
      >
        <div className="h-12 flex items-center justify-between px-5">
          <button type="button" onClick={() => { tap(); navigate('/'); }} aria-label="Home">
            <img
              src="/cscore-logo.png"
              alt="CScore"
              style={{ height: '20px', filter: 'brightness(0) saturate(100%) invert(13%) sepia(90%) saturate(5500%) hue-rotate(270deg) brightness(80%) contrast(110%)' }}
            />
          </button>
          <button type="button" onClick={() => { tap(); navigate('/settings'); }} aria-label="Settings">
            <Avatar src={profile?.profile_picture_url} name={`${profile?.first_name || ''} ${profile?.last_name || ''}`} size="sm" />
          </button>
        </div>
      </header>

      <Sidebar user={profile} />

      <main className="flex-1 min-w-0 md:ml-[248px] px-4 md:px-10 md:pt-8 md:pb-16">
        <div className="md:hidden" style={{ height: 'calc(48px + env(safe-area-inset-top))' }} />
        <div className="md:max-w-[1120px] md:mx-auto">
          <Outlet />
        </div>
        <div className="md:hidden" style={{ height: 'calc(104px + env(safe-area-inset-bottom))' }} />
      </main>

      {/* Phone tab bar: floating, frosted */}
      <nav
        className="md:hidden fixed left-4 right-4 z-20"
        style={{ bottom: 'calc(env(safe-area-inset-bottom) + 10px)' }}
      >
        <div className="h-[64px] flex items-center px-1.5 rounded-[26px] bg-white/80 backdrop-blur-xl border border-white/60 shadow-[0_8px_30px_rgba(17,12,34,0.14)]">
          {mobileTabs.map(({ path, label, icon: Icon }) => (
            <NavLink
              key={path}
              to={path}
              end={path === '/'}
              onClick={tap}
              className="flex-1 h-[52px] outline-none"
            >
              {({ isActive }) => (
                <span
                  className={`h-full mx-0.5 rounded-[20px] flex flex-col items-center justify-center gap-0.5 text-[10.5px] font-semibold transition-colors ${
                    isActive ? 'bg-primary-10 text-primary' : 'text-gray-500'
                  }`}
                >
                  <Icon size={23} weight={isActive ? 'fill' : 'regular'} />
                  {label}
                </span>
              )}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
